<script setup>
import { ref, getCurrentInstance, computed, nextTick, onMounted, onBeforeUnmount, watch } from 'vue'
import { DriverStore } from '../pinia/Driver.js'
import SKliveryCard from '../components/skeletons/SKliveryCard.vue'

const { proxy } = getCurrentInstance()
const liveries = ref([])  // All liveries from API
const carList = ref([])  // List of cars fetched from the API
const carfiltre = ref('')  // Selected car for filtering
const sortOrder = ref('desc')  // Sort order, default is descending
const isImagePopupOpen = ref(false)  // Contrôle l'ouverture de la popup
const isEditMode = ref(false)
const driver = DriverStore()
const isLoading = ref(true)  // Loading state

const LIVERY_CDN = 'https://cdn.nightriderz.world/images/website/liveries/'

async function loadLiveries(fetchLiveries) {
  isLoading.value = true
  liveries.value = []

  try {
    const res = await fetchLiveries()
    liveries.value = Array.isArray(res) ? res : []
  } finally {
    isLoading.value = false
  }
}

// Fetch existing liveries
async function getLiveries() {
  await loadLiveries(() => proxy.$api({ serviceName: "liveries", methodName: "getLiveries" }))
}

const selectedLivery = ref({ imageUrl: '', title: '', code: '', author: '', car: '' }); // Store livery data for the popup

function openImagePopup(livery) {
  selectedLivery.value = {
    imageUrl: `${LIVERY_CDN}${livery.code}.png`,
    title: livery.liveryname,
    code: livery.code,
    author: livery.personaName,
    car: livery.carFullName
  };
  isImagePopupOpen.value = true;
}


// Fetch the list of cars for filtering
async function getCarClasses() {
  const res = await proxy.$api({ serviceName: "liveries", methodName: "getCarClasses" })
  if (res && Array.isArray(res)) {
    carList.value = res;  // No need to map, API already returns the correct structure
  }
}

const isFormOpen = ref(false)
const isHowToOpen = ref(false)  // State for the "how to" popup
const form = ref({
  liveryName: '',
  liveryCode: '',
  liveryImage: null, // The selected file will be stored here
})

const previewImage = ref(null) // Stores the image URL for preview
const errorMessage = ref('')

function publishLivery() {
  resetForm();
  isFormOpen.value = true;
}

// Open "how to" dialog
function openHowTo() {
  isHowToOpen.value = true;
}

// Fetch liveries by selected carname (store_name)
async function getLiveriesByCar(carname) {
  await loadLiveries(() => proxy.$api({
    serviceName: "liveries",
    methodName: "getLiveriesByCar",
    parameters: [carname]
  }))
}

// Apply the car filter and fetch filtered liveries from the API
function CarFilter(option) {
  if (option) {
    carfiltre.value = option.store_name;
    getLiveriesByCar(carfiltre.value);
  } else {
    carfiltre.value = '';
    getLiveries();
  }
}

function openEditForm(livery) {
  resetForm();
  if (livery.personaId === driver.persona.ID) {
    form.value.liveryName = livery.liveryname
    form.value.liveryCode = livery.code  // Préremplir le code de la livrée
    isEditMode.value = true  // Mode édition
    isFormOpen.value = true
  } else {
    console.log('Vous ne pouvez pas éditer cette livrée.')
  }
}

function handleImageSelected(event) {
  const file = event.target.files[0];

  if (file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      previewImage.value = e.target.result;

      const img = new Image();
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        const ratio = (width / height).toFixed(2);
        const expectedRatio = (16 / 9).toFixed(2);

        if (ratio !== expectedRatio) {
          errorMessage.value = "Please select an image with a 16:9 ratio."
          previewImage.value = null;
        } else {
          errorMessage.value = '';
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
    form.value.liveryImage = file;
  } else {
    previewImage.value = null;
  }
}

function toggleSortOrder() {
  sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc';
}

// Computed property for sorted liveries
const sortedLiveries = computed(() => {
  return [...liveries.value].sort((a, b) => {
    if (sortOrder.value === 'asc') {
      return a.useCount - b.useCount;
    } else {
      return b.useCount - a.useCount;
    }
  });
});

function resetForm() {
  form.value = {
    liveryName: '',
    liveryCode: '',
    liveryImage: null,
  };
  previewImage.value = null;
  errorMessage.value = '';
  isEditMode.value = false;
}

function uploadLivery(e) {
  e.preventDefault();

  if (!form.value.liveryName || !form.value.liveryCode) {
    errorMessage.value = 'Veuillez fournir toutes les informations requises.';
    return;
  }

  // En mode création, l'image est obligatoire
  if (!isEditMode.value && !form.value.liveryImage) {
    errorMessage.value = 'Veuillez sélectionner une image pour la nouvelle livrée.';
    return;
  }

  const fd = new FormData();

  // Ajouter l'image uniquement si elle est présente (non obligatoire en mode édition)
  if (form.value.liveryImage) {
    fd.append("file", form.value.liveryImage);
  }

  fd.append('easharpptr-u', localStorage.getItem('easharpptr_u'));
  fd.append('easharpptr-p', localStorage.getItem('easharpptr_p'));
  fd.append('livery_code', form.value.liveryCode);
  fd.append('livery_name', form.value.liveryName);

  const url = isEditMode.value 
    ? 'https://api.nightriderz.world/update_livery.php'
    : 'https://api.nightriderz.world/upload_livery.php';

  var XHR = new XMLHttpRequest();
  XHR.onreadystatechange = function () {
    if (this.readyState == 4) {
      if (this.status == 200) {
        const response = JSON.parse(this.responseText);
        if (response.success) {
          getLiveries();  // Recharge les livrées après succès
          isFormOpen.value = false;
          previewImage.value = null;
        } else {
          errorMessage.value = response.message;
        }
      } else {
        errorMessage.value = 'Erreur lors de l\'envoi de la livrée.';
      }
    }
  };

  XHR.open('POST', url);
  XHR.send(fd);
}

function openFile() {
  document.querySelector('.file').click();
}

const copiedCode = ref(null)

async function copyLiveryCommand(code) {
  await navigator.clipboard.writeText(`/livery import ${code}`)
  copiedCode.value = code
  setTimeout(() => {
    if (copiedCode.value === code) copiedCode.value = null
  }, 2000)
}

function refreshAutoScrollValues() {
  nextTick(() => {
    const rows = document.querySelectorAll('.detail-value')

    rows.forEach((row) => {
      const text = row.querySelector('.detail-value-text')
      if (!text) return

      const overflowDistance = text.scrollWidth - row.clientWidth
      if (overflowDistance > 0) {
        row.classList.add('is-overflowing')
        row.style.setProperty('--scroll-distance', `${overflowDistance + 12}px`)
      } else {
        row.classList.remove('is-overflowing')
        row.style.removeProperty('--scroll-distance')
      }
    })
  })
}

function onWindowResize() {
  refreshAutoScrollValues()
}

// Initial call to load liveries and car list
getLiveries();
getCarClasses();

onMounted(() => {
  refreshAutoScrollValues()
  window.addEventListener('resize', onWindowResize)
})

watch([liveries, sortOrder], () => {
  refreshAutoScrollValues()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResize)
})
</script>

<template>
  <v-app-bar :elevation="2" class="modern-app-bar" style="overflow: initial;">
    <v-icon class="mr-3">mdi-palette</v-icon>
    <div class="app-bar-content">
      <v-app-bar-title class="app-bar-main-title">Liveries</v-app-bar-title>
      <div class="app-bar-subtitle">Browse and share custom car designs</div>
    </div>
    <v-spacer></v-spacer>
    <v-select 
      placeholder="Choose a car to filter results" 
      class="search" 
      density="compact" 
      solo 
      label="full_name" 
      :options="carList" 
      item-text="full_name" 
      item-value="store_name" 
      style="height: 32px;width: 420px;" 
      @update:modelValue="CarFilter"
    >
      <template v-slot:option="option">
        <div class="d-flex" style="justify-content: center;align-items: center;">
          <span style="width: 340px;text-overflow: ellipsis;overflow: hidden;text-transform: uppercase;">{{ option.full_name }}</span>
          <v-spacer></v-spacer>
          <v-chip size="small">{{ number_format(option.livery_count) }}</v-chip>
        </div>
      </template>
    </v-select>

    <v-btn size="small" @click="toggleSortOrder" style="background: #1f2430;border-radius: 4px;width: 32px; height: 32px; margin-left: 8px; min-width: 32px">
      <v-icon style="font-size: 20px;">{{ sortOrder === 'desc' ? 'mdi-sort-descending' : 'mdi-sort-ascending' }}</v-icon>
      <v-tooltip activator="parent" location="bottom">Invert sort order</v-tooltip>
    </v-btn>
    <!-- Publish Button -->
    <v-btn size="small" style="background: #1f2430; border-radius: 4px; width: 32px; height: 32px; margin-left: 8px; min-width: 32px" @click="publishLivery">
      <v-icon style="font-size: 20px;">mdi-publish</v-icon>
      <v-tooltip activator="parent" location="bottom">Publish a livery</v-tooltip>
    </v-btn>

    <!-- How to Button -->
    <v-btn size="small" style="background: #1f2430;border-radius: 4px;width: 32px; height: 32px; margin-left: 8px; min-width: 32px" @click="openHowTo">
      <v-icon style="font-size: 20px;">mdi-help-circle-outline</v-icon>
      <v-tooltip activator="parent" location="bottom">How to use</v-tooltip>
    </v-btn>
  </v-app-bar>

  <!-- Publish Livery Dialog -->
  <v-dialog v-model="isFormOpen" max-width="600px" scrollable>
    <v-card>
      <v-toolbar dark color="primary" class="livery-toolbar">
        <v-icon class="mr-2">mdi-palette</v-icon>
        <div class="toolbar-content">
          <v-toolbar-title class="toolbar-main-title">{{ isEditMode ? 'Edit a livery' : 'Publish a livery' }}</v-toolbar-title>
          <div class="toolbar-subtitle">{{ isEditMode ? 'Update your livery details' : 'Share your custom design with the community' }}</div>
        </div>
      </v-toolbar>
      <v-card-text>
        <v-form @submit.prevent="uploadLivery">
          <v-alert v-if="errorMessage" type="error">{{ errorMessage }}</v-alert>
          <v-text-field v-model="form.liveryName" label="Livery name" variant="outlined" required prepend-icon="mdi-tag-text"></v-text-field>
          <v-text-field v-model="form.liveryCode" label="Livery code" required variant="outlined" prepend-icon="mdi-qrcode" @input="form.liveryCode = form.liveryCode.toUpperCase()" :rules="[v => /^[A-Z0-9]{3}-[A-Z0-9]{3}-[A-Z0-9]{3}$/.test(v) || 'The code must be in the format ABC-123-DEF']" :disabled="isEditMode"></v-text-field>
          <input type="file" id="fileupload" class="file" @change="handleImageSelected" hidden>
          <img v-if="previewImage" :src="previewImage" alt="Image Preview" style="border-radius: 4px !important;position: fixed!important;width: 100px;top: 4px;right: 4px;"></img>
          <div class="d-flex justify-end">
            <v-btn @click="openFile" variant="tonal">Select an Image</v-btn>
            <v-btn type="submit" style="margin-left: 6px;background: rgb(38, 107, 173);">{{ isEditMode ? 'Save Changes' : 'Publish' }}</v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>


  <!-- How to Dialog -->
  <v-dialog v-model="isHowToOpen" max-width="550px" scrollable>
    <v-card>
      <v-toolbar dark color="primary" class="howto-toolbar">
        <v-icon class="mr-2">mdi-help-circle-outline</v-icon>
        <div class="toolbar-content">
          <v-toolbar-title class="toolbar-main-title">Livery System Usage</v-toolbar-title>
          <div class="toolbar-subtitle">Learn how to export and publish liveries</div>
        </div>
      </v-toolbar>
      <v-card-text>
        <h3>How to export and publish a livery?</h3>
        <ol>
          <li>In game, pick the car that has the livery you want to export.</li>
          <li>Open the chat and type <span style="background: black;font-size:14px;font-weight: 600;border-radius: 4px;"> /livery export </span> to get the <strong>code</strong>.</li>
          <li>Click on the <strong>upload</strong> button that is next to the car filter.</li>
          <li>Fill the <strong>form</strong> as it is indicated.</li>
          <li>Select an image of <strong>16:9 aspect ratio</strong> of your livery.</li>
          <li>Once all details are filled, click <strong>Publish</strong> to publish the livery.</li>
          <br>
          <span>Only the owner of a code can publish the livery linked to it.</span>
          <br>
          <span>The ideal image resolution is <span style="background: black;font-size:14px;font-weight: 600;border-radius: 4px;"> 600 x 338 </span>.</span>
        </ol>
        <br>
        <h3>How to install a livery?</h3>
        <ol>
          <li>In game, pick the car that you want to equip of a livery.</li>
          <li>Find the livery <strong>code</strong> on the website.</li>
          <li>Open the chat and execute the command <span style="background: black;font-size:14px;font-weight: 600;border-radius: 4px;"> /livery import 123-ABC-456 </span> to install the chosen <strong>livery</strong>.</li>
          <li>Leave or go to <strong>safehouse</strong> in order to refresh the car look.</li>
          <br>
          <span>In order to install a <strong>livery</strong> on another car model than the one it is made for, use the command suffix <span style="background: black;font-size:14px;font-weight: 600;border-radius: 4px;"> --force </span>.</span>
        </ol>
      </v-card-text>
      <v-card-actions>
        <v-btn color="primary" @click="isHowToOpen = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="isImagePopupOpen" max-width="980" class="livery-preview-dialog">
    <v-card class="preview-dialog-card">
      <v-btn
        icon="mdi-close"
        size="small"
        class="preview-close-btn"
        variant="flat"
        @click="isImagePopupOpen = false"
      />

      <div class="preview-dialog-media">
        <v-img :src="selectedLivery.imageUrl" cover class="preview-dialog-image"></v-img>
      </div>

      <div class="preview-dialog-body">
        <div class="preview-dialog-header">
          <div class="preview-dialog-title-block">
            <h3 class="preview-dialog-title">{{ selectedLivery.title }}</h3>
            <div class="preview-dialog-subtitle">by {{ selectedLivery.author }}</div>
          </div>
          <span class="badge preview-car-badge">
            <v-icon size="13">mdi-car</v-icon>
            {{ selectedLivery.car }}
          </span>
        </div>

        <div class="command-wrap preview-command-wrap" :class="{ copied: copiedCode === selectedLivery.code }">
          <div class="command-header">
            <span class="command-label">In-game command</span>
          </div>
          <div class="command-row">
            <code class="command-text">/livery import {{ selectedLivery.code }}</code>
            <button
              type="button"
              class="copy-action"
              :class="{ copied: copiedCode === selectedLivery.code }"
              @click="copyLiveryCommand(selectedLivery.code)"
            >
              <v-icon size="16">
                {{ copiedCode === selectedLivery.code ? 'mdi-check' : 'mdi-content-copy' }}
              </v-icon>
            </button>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>

  <!-- Display of filtered liveries -->
  <div class="grid">
    <template v-if="isLoading">
      <SKliveryCard v-for="i in 12" :key="'sk-' + i" />
    </template>

    <template v-else>
      <article v-for="livery in sortedLiveries" :key="livery.code" class="livery-card">
        <button type="button" class="preview-panel" @click="openImagePopup(livery)">
          <img
            :src="`${LIVERY_CDN}${livery.code}.png`"
            class="preview-image"
            :alt="livery.liveryname"
            loading="lazy"
            decoding="async"
          />
          <div class="preview-overlay">
            <v-icon size="30">mdi-magnify</v-icon>
            <span>Preview</span>
          </div>

          <div class="preview-badges">
            <span class="badge installs-badge">
              <v-icon size="13">mdi-download</v-icon>
              {{ number_format(livery.useCount) }}
            </span>
            <v-btn
              v-if="livery.personaId === driver.persona.ID"
              @click.stop="openEditForm(livery)"
              icon="mdi-pencil"
              size="x-small"
              variant="flat"
              class="edit-btn"
            >
              <v-tooltip activator="parent" location="bottom">Edit livery</v-tooltip>
            </v-btn>
          </div>
        </button>

        <div class="card-body">
          <div class="title-row">
            <h3 class="livery-title">{{ livery.liveryname }}</h3>
          </div>

          <div class="detail-list">
            <div class="detail-row">
              <span class="detail-label">Vehicle</span>
              <span class="detail-value">
                <span class="detail-value-text">{{ livery.carFullName }}</span>
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Author</span>
              <span class="detail-value">
                <span class="detail-value-text">{{ livery.personaName }}</span>
              </span>
            </div>
          </div>

          <div class="command-wrap" :class="{ copied: copiedCode === livery.code }">
            <div class="command-header">
              <span class="command-label">In-game command</span>
            </div>
            <div class="command-row">
              <code class="command-text">/livery import {{ livery.code }}</code>
              <button
                type="button"
                class="copy-action"
                :class="{ copied: copiedCode === livery.code }"
                @click="copyLiveryCommand(livery.code)"
              >
                <v-icon size="16">
                  {{ copiedCode === livery.code ? 'mdi-check' : 'mdi-content-copy' }}
                </v-icon>
              </button>
            </div>
          </div>
        </div>
      </article>
    </template>
  </div>
</template>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 22px;
  padding: 28px 18px 36px;
  max-width: 1400px;
  margin: 0 auto;
}

.livery-card {
  border-radius: 14px;
  min-height: 430px;
  overflow: hidden;
  background: linear-gradient(180deg, #1b2231 0%, #171b28 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

.livery-card:hover {
  transform: translateY(-5px);
  border-color: rgba(var(--v-theme-primary), 0.45);
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.3);
}

.preview-panel {
  position: relative;
  margin: 0;
  width: 100%;
  padding: 0;
  border: none;
  cursor: pointer;
  background: #111622;
  aspect-ratio: 16 / 9;
  overflow: hidden;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.35s ease, opacity 0.35s ease;
}

.livery-card:hover .preview-image {
  transform: scale(1.05);
}

.preview-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: rgba(0, 0, 0, 0.42);
  color: rgba(255, 255, 255, 0.95);
  text-transform: uppercase;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  opacity: 0;
  transition: opacity 0.25s ease;
}

.preview-panel:hover .preview-overlay {
  opacity: 1;
}

.preview-badges {
  position: absolute;
  top: 10px;
  left: 10px;
  right: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(9, 11, 16, 0.78);
  color: #fff;
  backdrop-filter: blur(6px);
}

.edit-btn {
  background: rgba(9, 11, 16, 0.78) !important;
  color: #fff !important;
  border: 1px solid rgba(255, 255, 255, 0.14);
}

.card-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.title-row {
  display: flex;
  align-items: center;
}

.livery-title {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  line-height: 1.25;
  min-width: 0;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.detail-list {
  display: grid;
  gap: 8px;
}

.detail-row {
  border-radius: 10px;
  padding: 9px 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  min-width: 0;
  overflow: hidden;
}

.detail-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
}

.detail-value {
  display: block;
  margin-top: 4px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  line-height: 1.3;
  position: relative;
}

.detail-value-text {
  display: inline-block;
  white-space: nowrap;
  transform: translateX(0);
}

.detail-value.is-overflowing .detail-value-text {
  animation: detail-auto-scroll 7s ease-in-out infinite alternate;
}

@keyframes detail-auto-scroll {
  0%,
  16% {
    transform: translateX(0);
  }
  84%,
  100% {
    transform: translateX(calc(-1 * var(--scroll-distance)));
  }
}

.command-wrap {
  border-radius: 10px;
  padding: 9px;
  background: rgba(12, 15, 22, 0.66);
  border: 1px solid rgba(var(--v-theme-primary), 0.24);
  transition: border-color 0.2s ease, background 0.2s ease;
}

.command-wrap.copied {
  border-color: rgba(129, 199, 132, 0.5);
  background: rgba(76, 175, 80, 0.1);
}

.command-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.command-label {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.56);
}

.command-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.command-text {
  min-width: 0;
  flex: 1;
  display: block;
  padding: 9px 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px dashed rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.95);
  font-family: 'Courier New', Consolas, monospace;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.copy-action {
  width: 38px;
  height: 38px;
  min-width: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: rgba(var(--v-theme-primary), 0.9);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.15s ease;
}

.copy-action:hover {
  filter: brightness(1.08);
}

.copy-action:active {
  transform: scale(0.96);
}

.copy-action.copied {
  background: #2e7d32;
}

.preview-dialog-card {
  position: relative;
  border-radius: 14px !important;
  overflow: hidden;
  background: linear-gradient(180deg, #1b2231 0%, #171b28 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.preview-close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 4;
  background: rgba(9, 11, 16, 0.82) !important;
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: #fff !important;
}

.preview-dialog-media {
  position: relative;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.preview-dialog-image {
  width: 100%;
  height: 100%;
}

.preview-dialog-body {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.preview-dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.preview-dialog-title-block {
  min-width: 0;
  flex: 1;
}

.preview-dialog-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  color: #fff;
  letter-spacing: 0.03em;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-dialog-subtitle {
  margin-top: 5px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.72);
}

.preview-car-badge {
  max-width: 45%;
  border-radius: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-command-wrap {
  margin-top: 2px;
}

@media (max-width: 920px) {
  .grid {
    grid-template-columns: 1fr;
    max-width: 520px;
  }

  .preview-dialog-header {
    flex-direction: column;
    gap: 8px;
  }

  .preview-car-badge {
    max-width: 100%;
  }
}

/* Old styles to keep */
.v-card-subtitle {
  background: var(--primary-color);
  display: flex;
  justify-content: center;
  padding: 10px 0;
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 6px;
  opacity: 1;
}

.v-select::v-deep(li) {
  margin: 0;
  overflow: hidden;
}

.v-select::v-deep(v-chip) {
  margin: 0;
  padding: 0;
}

.v-select::v-deep(button) {
  background: transparent;
}

.search {
  width: 450px;
  background: rgb(var(--v-theme-surface));
  border-radius: 4px;
  padding-right: 3px;
}

.v-img__img.v-img__img--contain {
  position: fixed !important;
}

/* Toolbar Styles */
.livery-toolbar,
.howto-toolbar {
  padding: 8px 16px !important;
}

.toolbar-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.toolbar-main-title {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  line-height: 1.2 !important;
}

.toolbar-subtitle {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1;
}

/* Modern App Bar Styles */
.modern-app-bar {
  padding: 8px 16px !important;
}

.app-bar-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  margin-right: 16px;
}

.app-bar-main-title {
  font-size: 1.4rem !important;
  font-weight: 600 !important;
  line-height: 1.2 !important;
}

.app-bar-subtitle {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1;
}
</style>
